import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EmployeeFilters = ({ onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    department: 'all',
    location: 'all',
    employmentType: 'all',
    status: 'all'
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const debounceTimer = React.useRef(null);

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const departments = [
    { value: 'all', label: 'All Departments' },
    { value: 'Security Services', label: 'Security Services' },
    { value: 'Housekeeping', label: 'Housekeeping' },
    { value: 'Maintenance', label: 'Maintenance' },
    { value: 'Administration', label: 'Administration' },
    { value: 'Human Resources', label: 'Human Resources' }
  ];

  const locations = [
    { value: 'all', label: 'All Locations' },
    { value: 'Mumbai', label: 'Mumbai' },
    { value: 'Delhi', label: 'Delhi' },
    { value: 'Bangalore', label: 'Bangalore' },
    { value: 'Pune', label: 'Pune' },
    { value: 'Chennai', label: 'Chennai' }
  ];

  const employmentTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'permanent', label: 'Permanent' },
    { value: 'contract', label: 'Contract' },
    { value: 'temporary', label: 'Temporary' },
    { value: 'probation', label: 'Probation' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const triggerFilters = (searchVal, filtersObj) => {
    onFilterChange({
      search: searchVal,
      ...filtersObj
    });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      triggerFilters(value, activeFilters);
    }, 800); // 800ms debounce
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Clear any pending timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      triggerFilters(searchTerm, activeFilters);
    }
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...activeFilters, [filterType]: value };
    setActiveFilters(newFilters);

    // If search is pending, clear and fire all together
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    triggerFilters(searchTerm, newFilters);
  };

  const clearAllFilters = () => {
    const resetFilters = {
      department: 'all',
      location: 'all',
      employmentType: 'all',
      status: 'all'
    };
    setActiveFilters(resetFilters);
    setSearchTerm('');
    triggerFilters('', resetFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(activeFilters).filter(value => value !== 'all').length;
  };

  const hasActiveFilters = getActiveFilterCount() > 0 || searchTerm !== '';

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      {/* Search Bar */}
      <div className="flex flex-col lg:flex-row gap-4 mb-4">
        <div className="flex-1">
          <div className="relative">
            <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search employees by name, ID, or email..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              className="pl-10 pr-10"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  triggerFilters('', activeFilters);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            iconName="Filter"
            iconPosition="left"
          >
            Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="outline"
              onClick={clearAllFilters}
              iconName="RotateCcw"
              className="text-muted-foreground"
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-border">
          {/* Department Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Department</label>
            <select
              value={activeFilters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {departments.map(dept => (
                <option key={dept.value} value={dept.value}>{dept.label}</option>
              ))}
            </select>
          </div>

          {/* Location Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Location</label>
            <select
              value={activeFilters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {locations.map(location => (
                <option key={location.value} value={location.value}>{location.label}</option>
              ))}
            </select>
          </div>

          {/* Employment Type Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Employment Type</label>
            <select
              value={activeFilters.employmentType}
              onChange={(e) => handleFilterChange('employmentType', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {employmentTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Status</label>
            <select
              value={activeFilters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeFilters;